import React from "react";
import { useTranslation } from "react-i18next";
import { Control } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";
import { TranslationType } from "../types";
import type { Country } from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import de from "react-phone-number-input/locale/de";
import en from "react-phone-number-input/locale/en";
import fr from "react-phone-number-input/locale/fr";
import nl from "react-phone-number-input/locale/nl";
import { languageMap } from "@/i18n";
import { BookingLanguage } from "@/types/booking";

interface PhoneFieldProps {
  control: Control<any>;
  name?: string;
  t: TranslationType;
  placeholder?: string;
  defaultCountry?: Country;
  language?: string;
}

export const PhoneField = ({
  control,
  name = "phone",
  t,
  placeholder,
  defaultCountry = "CH",
  language,
}: PhoneFieldProps) => {
  const lng = languageMap[(language as BookingLanguage) || "en-US"];
  const { t: i18n } = useTranslation(undefined, { lng });

  // Select the appropriate locale based on the current language
  const phoneLocale =
    language === "de-DE"
      ? de
      : language === "fr-FR"
        ? fr
        : language === "nl-NL"
          ? nl
          : en;

  // Translation for search placeholder
  const searchPlaceholder = i18n("bookingForm.searchCountry");
  const noCountryFound = i18n("bookingForm.noCountryFound");

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required: t.phoneRequired || "Phone number is required",
        validate: (value) => {
          if (!value) return t.phoneRequired || "Phone number is required";
          // Validate E.164 format using react-phone-number-input
          if (!isValidPhoneNumber(value)) {
            return (
              t.phoneInvalid ||
              "Please enter a valid phone number in international format"
            );
          }
          return true;
        },
      }}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t.phoneNumber}</FormLabel>
          <FormControl>
            <PhoneInput
              key={language} // Force remount when language changes
              aria-label={t.phoneNumber}
              placeholder={placeholder || "+41 XX XXX XX XX"}
              defaultCountry={defaultCountry}
              labels={phoneLocale}
              searchPlaceholder={searchPlaceholder}
              noCountryFound={noCountryFound}
              {...field}
              international={true}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
